import React, { useMemo, useState, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { FaChartBar, FaChartLine, FaDownload } from "react-icons/fa";
import { RiResetLeftLine } from "react-icons/ri";

const HeatmapChart = ({
  title,
  rowsData = [],
  colsData = [],
  widthOfTheChart,
  matrix = [],
  setSelectedCell,
  control,
  maxValue = null,
  minValue,
  rangeBarLength,
  colorScale,
  singleValue = false,
}) => {
  const [chartType, setChartType] = useState("bar");
  const chartRef = useRef(null);

  // SAFE matrix: if matrix is empty, a zero-matrix with shape
  const safeMatrix = useMemo(() => {
    const hasMatrixRows = Array.isArray(matrix) && matrix.length > 0;
    const rows = Array.isArray(rowsData) ? rowsData.length : 0;
    const cols = Array.isArray(colsData) ? colsData.length : 0;

    if (
      hasMatrixRows &&
      matrix.every((r) => Array.isArray(r) && r.length === cols)
    ) {
      return matrix;
    }

    // Default zero matrix preserving axes shape
    return Array.from({ length: rows }, () =>
      Array.from({ length: cols }, () => 0)
    );
  }, [matrix, rowsData, colsData]);

  // Heatmap data in [xIndex, yIndex, value] format
  const heatmapData = useMemo(() => {
    return (rowsData || []).flatMap((_, yIndex) =>
      (colsData || []).map((_, xIndex) => {
        const value = (safeMatrix[yIndex] && safeMatrix[yIndex][xIndex]) ?? 0;
        return [xIndex, yIndex, value];
      })
    );
  }, [rowsData, colsData, safeMatrix]);

  const computedMax = useMemo(() => {
    if (Number.isFinite(maxValue) && maxValue > 0) return maxValue;
    const flat = (safeMatrix || []).flat();
    const max = flat.length
      ? Math.max(...flat.map((v) => (Number.isFinite(v) ? v : 0)))
      : 0;
    return max <= 0 ? 1 : max;
  }, [safeMatrix, maxValue]);

  const option = useMemo(() => {
    return {
      title: {
        text: title,
        left: "center",
        textStyle: { color: "#333", fontSize: 14, fontWeight: "bold" },
      },
      tooltip: {
        formatter: (p) => {
          // params may be undefined if tooltip triggered strangely — defend
          if (!p || !p.data) return "";
          const x = p.data[0];
          const y = p.data[1];
          const val = p.data[2];
          const hour = (colsData && colsData[x]) || x;
          const lobby = (rowsData && rowsData[y]) || y;
          return `${lobby}<br>${hour}: <b>${val}</b>`;
        },
      },
      grid: {
        top: 50,
        left: 50,
        right: 50,
        height: "70%",
        width: widthOfTheChart || "100%",
      },
      xAxis: {
        type: "category",
        data: colsData || [],
        splitArea: { show: true },
        axisLabel: { interval: 0, fontSize: 13, color: "black" },
      },
      yAxis: {
        type: "category",
        data: rowsData || [],
        splitArea: { show: true },
        axisLabel: { interval: 0, fontSize: 13, color: "black" },
      },
      // legend: {
      //   top: 30,
      //   type: "scroll",
      //   orient: "horizontal",
      // },
      visualMap: {
        type: "continuous",
        min: minValue || 0,
        max: computedMax,
        calculable: true,
        realtime: true,
        orient: "horizontal",
        left: "center",
        itemWidth: 30,
        itemHeight: rangeBarLength ? rangeBarLength : 90,
        // margin: [0, 30, 0, 0],
        textStyle: { fontSize: 12 },
        bottom: 10,
        inRange: {
          color:
            Array.isArray(colorScale) && colorScale.length
              ? colorScale
              : [
                  "#fff6d5",
                  "#ffd57aff",
                  "#fdbe58ff",
                  "#ffae5dff",
                  "#ff9544ff",
                  "#f06f29ff",
                  "#e56b3fff",
                  "#e65741ff",
                  "#de3c36ff",
                  "#f32629e5",
                ],
        },
      },
      series: [
        {
          name: "Heatmap",
          type: "heatmap",
          data: Array.isArray(heatmapData) ? heatmapData : [],
          label: {
            show: true,
            color: "#000",
            fontSize: singleValue ? 12 : 11,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 12,
              shadowColor: "rgba(0,0,0,0.4)",
            },
          },
        },
      ],
    };
  }, [
    title,
    colsData,
    rowsData,
    heatmapData,
    computedMax,
    colorScale,
    singleValue,
  ]);

  // ------------------- DOWNLOAD -------------------
  const downloadChart = () => {
    try {
      const echartsInstance = chartRef.current?.getEchartsInstance?.();
      if (!echartsInstance) return;
      const imgData = echartsInstance.getDataURL({
        type: "png",
        pixelRatio: 2,
        backgroundColor: "#fff",
      });
      const link = document.createElement("a");
      link.href = imgData;
      link.download = `${title || "HeatMapChart"}.png`;
      link.click();
    } catch (e) {}
  };
  const onEvents = {
    click: (params) => {
      if (!params?.data) return;

      const [xIndex, yIndex, value] = params.data;

      const colLabel = colsData?.[xIndex] ?? xIndex;
      const rowLabel = rowsData?.[yIndex] ?? yIndex;

      setSelectedCell({
        row: rowLabel,
        column: colLabel,
        value,
      });
    },
  };

  return (
    <div>
      {!singleValue && control && (
        <div className="d-flex justify-content-end mt-1">
          <button
            className="btn btn-sm btn-outline-dark d-flex align-items-center me-1"
            style={{ height: "25px" }}
            onClick={downloadChart}
            title="Download PNG"
          >
            <FaDownload />
          </button>

          <button
            className={`btn btn-sm btn-outline-dark d-flex align-items-center me-1 ${
              chartType === "bar" ? "active" : ""
            }`}
            style={{ height: "25px" }}
            onClick={() => setChartType("bar")}
            title="Bar"
          >
            <FaChartBar />
          </button>
          <button
            className={`btn btn-sm btn-outline-dark d-flex align-items-center me-1 ${
              chartType === "line" ? "active" : ""
            }`}
            style={{ height: "25px" }}
            onClick={() => setChartType("line")}
            title="Line"
          >
            <FaChartLine />
          </button>
          <button
            className="btn btn-sm btn-outline-dark d-flex align-items-center me-1"
            style={{ height: "25px" }}
            onClick={() => setChartType("bar")}
            title="Reset"
          >
            <RiResetLeftLine />
          </button>
        </div>
      )}

      <ReactECharts
        ref={chartRef}
        option={option}
        notMerge={true}
        onEvents={onEvents}
        style={{ height: singleValue ? "250px" : "550px", width: "100%" }}
      />
    </div>
  );
};

export default HeatmapChart;
